import React from 'react';
import styled, { keyframes } from 'styled-components';
import { BodyText, TinyTitleBold, Stack, FocusTrap } from '@kvdbil/components';
import RegistrationPlateInput from '~/App/components/RegistrationPlateInput';
import { useTranslation } from '~/Locale';
import { UseRegistrationPlateSubmit } from '~/App/shared/hooks/useRegistrationPlateSubmit';

const expandFromButton = keyframes`
  from {
    opacity: 0;
    transform: scale(0.1);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Container = styled.div`
  position: absolute;
  bottom: 5rem;
  left: 0;
  width: 21rem;
  max-width: calc(100vw - 1.5rem);
  border-radius: 0.5rem;
  filter: drop-shadow(0 5px 10px rgba(0, 0, 0, 0.15));
  z-index: 1;
  cursor: auto;
  transform-origin: 1.875rem calc(100% + 3.125rem);
  animation: ${expandFromButton} 220ms cubic-bezier(0.16, 1, 0.3, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Header = styled(TinyTitleBold)`
  background-color: ${({ theme }) => theme.colors.primary.main};
  color: ${({ theme }) => theme.colors.text.dark};
  padding: 1rem;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
`;

const Body = styled.div`
  background-color: ${({ theme }) => theme.colors.background.light};
  color: ${({ theme }) => theme.colors.text.dark};
  padding: 1rem;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;

  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 2rem;
    width: 0;
    height: 0;
    border-top: 8px solid ${({ theme }) => theme.colors.background.light};
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
  }
`;

interface Props extends UseRegistrationPlateSubmit {
  onContainerClick?: (e: React.MouseEvent) => void;
}

const ValuationPopup = ({
  onSubmit,
  isLoading,
  error,
  onContainerClick
}: Props) => {
  const { t } = useTranslation();

  return (
    <Container onClick={onContainerClick}>
      <FocusTrap autoFocusOnOpen="none">
        <Header as="div">
          {t('Did you know you can sell your car with us?')}
        </Header>
        <Body>
          <Stack>
            <BodyText>
              {t(
                'Enter your reg.nr. and get a free valuation right away. Fast, simple, safe!'
              )}
            </BodyText>
            <RegistrationPlateInput
              title={t('Valuate')}
              onSubmit={onSubmit}
              isLoading={isLoading}
              inline
              submitColor="primary"
              inputProps={{
                hasError: Boolean(error),
                errors: error ? [error] : undefined
              }}
            />
          </Stack>
        </Body>
      </FocusTrap>
    </Container>
  );
};

export default ValuationPopup;
